import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2, Check, Info } from 'lucide-react';
import type { Plant } from '../../types/plant';
import { useGarden } from '../../hooks/useGarden';
import { useAuth } from '../../hooks/useAuth';

interface PlantListItemProps {
  plant: Plant;
  isInGarden: boolean;
  onAddSuccess: () => void;
}

export default function PlantListItem({ plant, isInGarden, onAddSuccess }: PlantListItemProps) {
  const { user } = useAuth();
  const { addToGarden, loading } = useGarden();
  const [error, setError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleAddToGarden = async (e: React.MouseEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      await addToGarden(plant.id);
      onAddSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to garden');
    }
  };

  return (
    <div 
      className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-lg group"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Link 
        to={`/plants/${plant.id}`}
        className="flex-1 flex items-center gap-3"
      >
        <div className="relative w-12 h-12 flex-shrink-0">
          <img 
            src={plant.image_url} 
            alt={plant.name}
            className="w-full h-full object-cover rounded-md"
          />
        </div>
        <div>
          <h3 className="font-medium group-hover:text-primary-600 transition-colors flex items-center gap-2">
            {plant.name}
            {isHovering && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Info className="h-3 w-3" />
                <span>More Information</span>
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-500 italic">{plant.scientific_name}</p>
        </div>
      </Link>

      {error && (
        <p className="text-sm text-red-600 mr-4">{error}</p>
      )}

      {!isInGarden ? (
        <button
          onClick={handleAddToGarden}
          disabled={loading || !user}
          className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Plus className="h-5 w-5" />
              <span>Add to Garden</span>
            </>
          )}
        </button>
      ) : (
        <div className="flex items-center gap-2 bg-gray-100 text-gray-600 px-4 py-2 rounded-md min-w-[140px] justify-center">
          <Check className="h-5 w-5" />
          <span>In Your Garden</span>
        </div>
      )}
    </div>
  );
}